'use client';

/**
 * PartnerStatsCard
 *
 * Renders the structured payload returned by `crm_get_partner_stats`.
 * Single-card stats summary for one partner (activity + performance).
 *
 * Issue #1432 Phase 2 — Wave 3 P2
 */

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { RendererEmptyState } from './RendererEmptyState';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const PartnerStatsSchema = z.object({
  partnerId: z.string().optional(),
  partnerName: z.string().optional(),
  activeDeals: z.number().optional(),
  mtdVolume: z.number().optional(),
  mtdCommission: z.number().optional(),
  closeRate: z.number().optional(),
  avgDaysToClose: z.number().optional(),
});

type PartnerStats = z.infer<typeof PartnerStatsSchema>;

const PartnerStatsWrapperSchema = z.object({
  stats: PartnerStatsSchema,
});

function parsePartnerStatsData(data: unknown): PartnerStats | null {
  const wrapped = PartnerStatsWrapperSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.stats;

  const direct = PartnerStatsSchema.safeParse(data);
  if (direct.success) return direct.data;

  return null;
}

// ============================================================================
// Subcomponents
// ============================================================================

interface FieldRowProps {
  label: string;
  children: ReactNode;
}

const FieldRow = ({ label, children }: FieldRowProps): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      py: 0.5,
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right' }}>{children}</Box>
  </Box>
);

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section = ({ title, children }: SectionProps): ReactElement => (
  <Box>
    <Typography variant="h6" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={0.25}>{children}</Stack>
  </Box>
);

// ============================================================================
// Component
// ============================================================================

export const PartnerStatsCard = ({ data }: AstorToolRendererProps): ReactElement => {
  const { t, formatCurrency, formatNumber } = useI18n();
  const parsed = useMemo(() => parsePartnerStatsData(data), [data]);

  if (!parsed) {
    return (
      <RendererEmptyState
        title={t('astorTools.partnerStats.empty')}
        description={t('astorTools.partnerStats.emptyDescription')}
      />
    );
  }

  const hasActivity = parsed.activeDeals != null || parsed.mtdVolume != null || parsed.mtdCommission != null;
  const hasPerformance = parsed.closeRate != null || parsed.avgDaysToClose != null;

  if (!hasActivity && !hasPerformance) {
    return (
      <RendererEmptyState
        title={t('astorTools.partnerStats.empty')}
        description={t('astorTools.partnerStats.emptyDescription')}
      />
    );
  }

  return (
    <Card variant="outlined" sx={{ my: 1.5, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={2.5}>
          {parsed.partnerName && <Typography variant="h6">{parsed.partnerName}</Typography>}

          {hasActivity && (
            <Section title={t('astorTools.partnerStats.activitySection')}>
              {typeof parsed.activeDeals === 'number' && (
                <FieldRow label={t('astorTools.partnerStats.activeDeals')}>
                  <Typography variant="body1">{formatNumber(parsed.activeDeals)}</Typography>
                </FieldRow>
              )}
              {typeof parsed.mtdVolume === 'number' && (
                <FieldRow label={t('astorTools.partnerStats.mtdVolume')}>
                  <Typography variant="body1">{formatCurrency(parsed.mtdVolume)}</Typography>
                </FieldRow>
              )}
              {typeof parsed.mtdCommission === 'number' && (
                <FieldRow label={t('astorTools.partnerStats.mtdCommission')}>
                  <Typography variant="body1">{formatCurrency(parsed.mtdCommission)}</Typography>
                </FieldRow>
              )}
            </Section>
          )}

          {hasPerformance && (
            <Section title={t('astorTools.partnerStats.performanceSection')}>
              {typeof parsed.closeRate === 'number' && (
                <FieldRow label={t('astorTools.partnerStats.closeRate')}>
                  <Typography variant="body1">{`${parsed.closeRate.toFixed(1)}%`}</Typography>
                </FieldRow>
              )}
              {typeof parsed.avgDaysToClose === 'number' && (
                <FieldRow label={t('astorTools.partnerStats.avgDays')}>
                  <Typography variant="body1">{formatNumber(Math.round(parsed.avgDaysToClose * 10) / 10)}</Typography>
                </FieldRow>
              )}
            </Section>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PartnerStatsCard;
